import React, { useState, useEffect } from 'react';
import { siteConfig } from '@/config/site.config';
import { siteContent } from '@/content/site';
import { Icon } from '@/components/ui';
import { useI18n } from '@/i18n';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const Navbar: React.FC = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 50) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close menu when resizing to desktop
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 1024) {
                setIsMenuOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Handle Esc key to close menu
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsMenuOpen(false);
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, []);

    // Body scroll lock
    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [isMenuOpen]);

    // Anchor links + `scroll-margin-top` (see `src/styles/theme.css` /
    // `App.css`) handle the smooth, offset scroll natively — no JS scroll
    // math needed. We only need to close the mobile menu on navigation.
    const closeMenu = () => setIsMenuOpen(false);

    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

    // Optional i18n scaffold (`src/i18n/`, disabled by default — see
    // `siteConfig.i18n`). `t()` falls back to the given label verbatim when
    // i18n is off or a key is missing, so this is a no-op until enabled.
    const { t } = useI18n();

    return (
        <nav
            className={`navbar ${isScrolled ? 'scrolled' : ''} ${isMenuOpen ? 'menu-open' : ''}`}
            id="navbar"
        >
            <a href="/" className="logo-group" onClick={closeMenu}>
                <img
                    src={siteConfig.brand.logo}
                    alt={siteContent.brand.logoAlt}
                    className="logo-img"
                />
                <div className="logo-text">
                    <span className="word-brilliance">{siteContent.brand.words.first}</span>
                    <span className="word-foods">{siteContent.brand.words.second}</span>
                </div>
            </a>

            <button
                className="nav-toggle"
                onClick={toggleMenu}
                aria-expanded={isMenuOpen}
                aria-label="Toggle navigation"
            >
                <Icon name={isMenuOpen ? 'x' : 'menu'} />
            </button>

            <ul className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
                {siteContent.navLinks.map((link) => (
                    <li key={link.id}>
                        <a href={link.id === 'home' ? '/' : `#${link.id}`} onClick={closeMenu}>
                            {t(`nav.${link.id}`, link.label)}
                        </a>
                    </li>
                ))}
                {/* Extra standalone pages (About/Blog, ...) — see `siteConfig.pages`.
                    Plain anchors, consistent with the section links above. */}
                {siteConfig.pages.map((page) => (
                    <li key={page.path}>
                        <a href={page.path} onClick={closeMenu}>
                            {page.label}
                        </a>
                    </li>
                ))}
                {siteConfig.i18n.enabled && (
                    <li>
                        <LanguageSwitcher />
                    </li>
                )}
            </ul>
        </nav>
    );
};

export default Navbar;
